import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { PencilIcon, TrashIcon, ClipboardDocumentIcon, UsersIcon, BuildingOffice2Icon, EyeIcon } from '@heroicons/react/24/outline';
import copy from 'clipboard-copy';

const API_BASE_URL = process.env.REACT_APP_API_URL;

const AdminDashboard = () => {
  const navigate = useNavigate();
  const tableRef = React.useRef(null);
  const searchTimeout = React.useRef(null);

  // User management state
  const [users, setUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [userSearchTerm, setUserSearchTerm] = useState('');
  const [selectedUser, setSelectedUser] = useState(null);
  const [showUserForm, setShowUserForm] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    role: 'user',
    subscription_date: ''
  });

  // Company management state
  const [companies, setCompanies] = useState([]); // Store searched companies
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [showCompanyForm, setShowCompanyForm] = useState(false);
  const [companyFormData, setCompanyFormData] = useState({
    name: '',
    company_number: '',
  });

  // Product management state
  const [products, setProducts] = useState([]);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [showProductForm, setShowProductForm] = useState(false);
  const [companyList, setCompanyList] = useState([]);
  const [productSearchQuery, setProductSearchQuery] = useState('');
  const [productFormData, setProductFormData] = useState({
    name: '',
    companies: []
  });

  // Company search state for product form
  const [companySearchQuery, setCompanySearchQuery] = useState('');
  const [filteredCompanyList, setFilteredCompanyList] = useState([]);

  // Common state
  const [activeTab, setActiveTab] = useState('users');
  const [selectedFile, setSelectedFile] = useState(null);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [error, setError] = useState('');

  // Sorting state
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });

  const fetchUsers = React.useCallback(async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/admin/users`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
      });
      setUsers(response.data);
      setFilteredUsers(response.data); // Initialize filtered users with all users
    } catch (err) {
      setError(err.response?.data?.error || 'Error fetching users');
    }
  }, []);

  const fetchProducts = React.useCallback(async () => {
    try {
      let url = productSearchQuery
        ? `${API_BASE_URL}/api/admin/products/search?query=${encodeURIComponent(productSearchQuery)}`
        : `${API_BASE_URL}/api/admin/products`;

      const response = await axios.get(url, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
      });
      setProducts(response.data);
    } catch (err) {
      setError(err.response?.data?.error || 'Error fetching products');
    }
  }, [productSearchQuery]);

  const itemsPerPage = 10;

  const fetchCompanies = React.useCallback(async (pageNum = 1, search = '') => {
    try {
      if (search && search.length < 2) return;
      
      const url = `${API_BASE_URL}/api/admin/companies`;
      const response = await axios.get(url, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
      });
      
      let filteredData = response.data;
      if (search) {
        const searchLower = search.toLowerCase();
        filteredData = response.data.filter(company => 
          company.company_name.toLowerCase().includes(searchLower)
        );
      }
      
      const start = (pageNum - 1) * itemsPerPage;
      const end = start + itemsPerPage;
      const paginatedData = filteredData.slice(start, end);
      
      setCompanies(prev => pageNum === 1 ? paginatedData : [...prev, ...paginatedData]);
      setHasMore(end < filteredData.length);
      setPage(pageNum);
      setError('');
    } catch (err) {
      console.error('Error fetching companies:', err);
      setError(err.response?.data?.error || 'Error fetching companies');
      setHasMore(false);
    }
  }, [itemsPerPage]);

  const fetchCompanyList = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/admin/companies/list`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
      });
      setCompanyList(response.data);
    } catch (err) {
      setError(err.response?.data?.error || 'Error fetching companies list');
    }
  };

  // fetchProducts is already defined as a useCallback hook above

  // Add a debounced search effect with minimum character check
  useEffect(() => {
    let timer;
    
    if (activeTab === 'products' && productSearchQuery) {
      // Skip search if query is too short
      if (productSearchQuery.length < 2) {
        setProducts([]);
        return;
      }

      setLoading(true);
      timer = setTimeout(() => {
        fetchProducts().finally(() => setLoading(false));
      }, 500);
    }

    return () => {
      if (timer) {
        clearTimeout(timer);
      }
    };
  }, [productSearchQuery, activeTab, fetchProducts]);

  const resetFormState = () => {
    setFormData({
      email: '',
      password: '',
      role: 'user',
      subscription_date: ''
    });
    setSelectedUser(null);
    setShowUserForm(false);
  };

  const handleCreateUser = async (e) => {
    e.preventDefault();
    try {
      await axios.post(
        `${API_BASE_URL}/api/admin/users`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`,
          },
        }
      );
      resetFormState();
      fetchUsers();
    } catch (err) {
      setError(err.response?.data?.error || 'Error creating user');
    }
  };

  const handleUpdateUser = async (e) => {
    e.preventDefault();
    try {
      // If subscription_date is empty and we're updating, don't include it in the request
      const updateData = { ...formData };
      if (!updateData.subscription_date) {
        delete updateData.subscription_date;
      }
      
      await axios.put(
        `${API_BASE_URL}/api/admin/users/${selectedUser.id}`,
        updateData,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`,
          },
        }
      );
      setShowUserForm(false);
      setSelectedUser(null);
      setFormData({ email: '', password: '', role: 'user', subscription_date: '' });
      fetchUsers();
    } catch (err) {
      setError(err.response?.data?.error || 'Error updating user');
    }
  };

  const handleDeleteUser = async (userId) => {
    if (window.confirm('Are you sure you want to delete this user?')) {
      try {
        await axios.delete(`${API_BASE_URL}/api/admin/users/${userId}`, {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`,
          },
        });
        fetchUsers();
      } catch (err) {
        setError(err.response?.data?.error || 'Error deleting user');
      }
    }
  };

  const handleEditUser = (user) => {
    setSelectedUser(user);
    setFormData({
      email: user.email,
      password: '',
      role: user.role,
      subscription_date: user.subscription_date ? new Date(user.subscription_date).toISOString().split('T')[0] : ''
    });
    setShowUserForm(true);
    // Smooth scroll to top
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleCompanySubmit = async (e) => {
    e.preventDefault();
    const formData = new FormData();
    formData.append('name', companyFormData.name);
    formData.append('company_number', companyFormData.company_number || '0');
    if (selectedFile) {
      formData.append('pdf_file', selectedFile);
    }

    try {
      if (selectedCompany) {
        await axios.put(
          `${API_BASE_URL}/api/admin/companies/${selectedCompany.id}`,
          formData,
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem('token')}`,
              'Content-Type': 'multipart/form-data',
            },
          }
        );
      } else {
        await axios.post(
          `${API_BASE_URL}/api/admin/companies`,
          formData,
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem('token')}`,
              'Content-Type': 'multipart/form-data',
            },
          }
        );
      }
      setShowCompanyForm(false);
      setSelectedCompany(null);
      setCompanyFormData({ name: '', company_number: '' });
      setSelectedFile(null);
      fetchCompanies();
    } catch (err) {
      setError(err.response?.data?.error || 'Error saving company');
    }
  };

  const handleDeleteCompany = async (companyId) => {
    if (window.confirm('Are you sure you want to delete this company?')) {
      try {
        await axios.delete(`${API_BASE_URL}/api/admin/companies/${companyId}`, {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`,
          },
        });
        fetchCompanies();
      } catch (err) {
        setError(err.response?.data?.error || 'Error deleting company');
      }
    }
  };

  const handleEditCompany = (company) => {
    setSelectedCompany(company);
    setCompanyFormData({
      name: company.company_name,
    });
    setShowCompanyForm(true);
  };



  const generateStrongPassword = () => {
    const length = 12;
    const charset = 'abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789!@#$%^&*()_+';
    let password = '';
    for (let i = 0; i < length; i++) {
      const randomIndex = Math.floor(Math.random() * charset.length);
      password += charset[randomIndex];
    }
    setFormData({ ...formData, password });
  };

  const handleCopyPassword = async () => {
    if (formData.password) {
      try {
        await copy(formData.password);
        // You could add a toast notification here
      } catch (err) {
        console.error('Failed to copy password:', err);
      }
    }
  };

  const handleCreateProduct = async (e) => {
    e.preventDefault();
    try {
      // First check if a product with this name already exists
      const checkResponse = await axios.get(
        `${API_BASE_URL}/api/admin/products/search?query=${encodeURIComponent(productFormData.name)}`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`,
          },
        }
      );

      const existingProduct = checkResponse.data.find(
        product => product.name.toLowerCase() === productFormData.name.toLowerCase()
      );

      if (existingProduct) {
        setError('A product with this name already exists');
        return;
      }

      // If no duplicate found, create the product
      await axios.post(
        `${API_BASE_URL}/api/admin/products`,
        productFormData,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`,
          },
        }
      );
      setShowProductForm(false);
      setProductFormData({ name: '', companies: [] });
      fetchProducts();
      setError(''); // Clear any existing errors
    } catch (err) {
      setError(err.response?.data?.error || 'Error creating product');
    }
  };

  const handleUpdateProduct = async (e) => {
    e.preventDefault();
    try {
      // Check if a product with this name already exists (excluding the current product)
      const checkResponse = await axios.get(
        `${API_BASE_URL}/api/admin/products/search?query=${encodeURIComponent(productFormData.name)}`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`,
          },
        }
      );

      const existingProduct = checkResponse.data.find(
        product => product.name.toLowerCase() === productFormData.name.toLowerCase() 
          && product.id !== selectedProduct.id
      );

      if (existingProduct) {
        setError('A product with this name already exists');
        return;
      }

      // If no duplicate found, update the product
      await axios.put(
        `${API_BASE_URL}/api/admin/products/${selectedProduct.id}`,
        productFormData,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`,
          },
        }
      );
      setShowProductForm(false);
      setSelectedProduct(null);
      setProductFormData({ name: '', companies: [] });
      fetchProducts();
      setError(''); // Clear any existing errors
    } catch (err) {
      setError(err.response?.data?.error || 'Error updating product');
    }
  };

  const handleDeleteProduct = async (productId) => {
    if (window.confirm('Are you sure you want to delete this product?')) {
      try {
        await axios.delete(`${API_BASE_URL}/api/admin/products/${productId}`, {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`,
          },
        });
        fetchProducts();
      } catch (err) {
        setError(err.response?.data?.error || 'Error deleting product');
      }
    }
  };

  const handleEditProduct = async (product) => {
    try {
      const response = await axios.get(
        `${API_BASE_URL}/api/admin/products/${product.id}`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`,
          },
        }
      );
      const productDetails = response.data;
      setSelectedProduct(productDetails);

      let companiesData = [];
      try {
        if (productDetails.companies) {
          companiesData = typeof productDetails.companies === 'string'
            ? JSON.parse(productDetails.companies).map(c => c.id)
            : productDetails.companies.map(c => c.id);
        }
      } catch (parseErr) {
        console.error('Error parsing companies data:', parseErr);
      }

      setProductFormData({
        name: productDetails.name,
        companies: companiesData
      });
      setShowProductForm(true);
    } catch (err) {
      setError(err.response?.data?.error || 'Error fetching product details');
    }
  };

  const handleSearch = (value) => {
    if (searchTimeout.current) {
      clearTimeout(searchTimeout.current);
    }
    
    setSearchTerm(value);
    setCompanies([]);
    setPage(1);
    setHasMore(true);
    
    if (!value || value.length < 2) {
      return;
    }
    
    searchTimeout.current = setTimeout(() => {
      setLoading(true);
      fetchCompanies(1, value)
        .finally(() => setLoading(false));
    }, 300);
  };

  useEffect(() => {
    if (companyList.length > 0) {
      setFilteredCompanyList(
        companyList.filter(company =>
          company.company_name.toLowerCase().includes(companySearchQuery.toLowerCase())
        )
      );
    }
  }, [companySearchQuery, companyList]);

  // Filter users based on search term
  const sortData = useCallback((data) => {
    if (!sortConfig.key) return data;
    
    return [...data].sort((a, b) => {
      if (sortConfig.key === 'email') {
        return sortConfig.direction === 'asc' 
          ? a.email.localeCompare(b.email)
          : b.email.localeCompare(a.email);
      }
      if (sortConfig.key === 'created_at') {
        return sortConfig.direction === 'asc'
          ? new Date(a.created_at) - new Date(b.created_at)
          : new Date(b.created_at) - new Date(a.created_at);
      }
      return 0;
    });
  }, [sortConfig]);

  useEffect(() => {
    if (users.length > 0) {
      let filtered = users;
      if (userSearchTerm) {
        const searchLower = userSearchTerm.toLowerCase();
        filtered = users.filter(user => 
          user.email.toLowerCase().includes(searchLower) ||
          user.role.toLowerCase().includes(searchLower)
        );
      }
      const sortedUsers = sortData(filtered);
      setFilteredUsers(sortedUsers);
    }
  }, [users, userSearchTerm, sortData]);

  // Sort function
  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  // Sort users based on current sort configuration
  const getSortedUsers = (users) => {
    if (!sortConfig.key) return users;

    return [...users].sort((a, b) => {
      if (sortConfig.key === 'email') {
        return sortConfig.direction === 'asc' 
          ? a.email.localeCompare(b.email)
          : b.email.localeCompare(a.email);
      }
      if (sortConfig.key === 'created_at') {
        return sortConfig.direction === 'asc'
          ? new Date(a.created_at) - new Date(b.created_at)
          : new Date(b.created_at) - new Date(a.created_at);
      }
      return 0;
    });
  };

  useEffect(() => {
    if (activeTab === 'users') {
      fetchUsers();
    } else if (activeTab === 'products') {
      fetchProducts();
    }
  }, [activeTab, fetchProducts, fetchUsers]);

  useEffect(() => {
    if (activeTab === 'companies' && searchTerm && searchTerm.length >= 2 && !loading) {
      const options = {
        root: null,
        rootMargin: '100px',
        threshold: 0.1
      };

      const handleObserver = (entries) => {
        const [target] = entries;
        if (target.isIntersecting && hasMore) {
          const nextPage = page + 1;
          setLoading(true);
          fetchCompanies(nextPage, searchTerm)
            .finally(() => setLoading(false));
        }
      };

      const currentObserver = new IntersectionObserver(handleObserver, options);
      const element = tableRef.current;
      
      if (element) {
        currentObserver.observe(element);
      }

      return () => {
        if (element) {
          currentObserver.unobserve(element);
        }
      };
    }
  }, [hasMore, loading, page, activeTab, searchTerm, fetchCompanies]);

  const handleShowProductForm = () => {
    setSelectedProduct(null);
    setProductFormData({ name: '', companies: [] });
    setProductSearchQuery(''); // Clear search query
    setProducts([]); // Clear search results
    setShowProductForm(true);
  };

  return (
    <div className="min-h-screen bg-gray-900 text-gray-100">
      <nav className="bg-gray-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <h1 className="text-xl font-bold text-yellow-500">Admin Dashboard</h1>
            <button
              onClick={() => {
                localStorage.removeItem('token');
                navigate('/');
              }}
              className="text-gray-300 hover:text-yellow-500 transition-colors duration-200"
            >
              Logout
            </button>
          </div>
        </div>
      </nav>

      <main className="max-w-7xl mx-auto py-6 sm:px-6 lg:px-8">
        <div className="px-4 py-6 sm:px-0">
          {/* Tab Navigation */}
          <div className="flex border-b border-gray-700 mb-6">
            <button
              onClick={() => setActiveTab('users')}
              className={`py-2 px-4 ${
                activeTab === 'users'
                  ? 'border-b-2 border-yellow-500 text-yellow-500'
                  : 'text-gray-400 hover:text-yellow-500'
              }`}
            >
              <div className="flex items-center gap-2">
                <UsersIcon className="h-5 w-5" />
                Users
              </div>
            </button>
            <button
              onClick={() => setActiveTab('companies')}
              className={`py-2 px-4 ${
                activeTab === 'companies'
                  ? 'border-b-2 border-yellow-500 text-yellow-500'
                  : 'text-gray-400 hover:text-yellow-500'
              }`}
            >
              <div className="flex items-center gap-2">
                <BuildingOffice2Icon className="h-5 w-5" />
                Companies
              </div>
            </button>
            <button
              onClick={() => {
                setActiveTab('products');
                fetchCompanyList(); // Load companies for the product form dropdown
              }}
              className={`py-2 px-4 ${
                activeTab === 'products'
                  ? 'border-b-2 border-yellow-500 text-yellow-500'
                  : 'text-gray-400 hover:text-yellow-500'
              }`}
            >
              <div className="flex items-center gap-2">
                <BuildingOffice2Icon className="h-5 w-5" />
                Products
              </div>
            </button>
          </div>

          {/* Users Management */}
          {activeTab === 'users' && (
            <div>
              <div className="space-y-6">
                <div className="flex justify-between items-center">
                  <h2 className="text-2xl font-bold text-yellow-500">Manage Users</h2>
                  <button
                    onClick={() => {
                      setSelectedUser(null);
                      setFormData({ email: '', password: '', role: 'user' });
                      setShowUserForm(true);
                    }}
                    className="bg-yellow-500 text-gray-900 px-4 py-2 rounded-md hover:bg-yellow-600 transition-colors duration-200"
                  >
                    Add New User
                  </button>
                </div>

                {/* Search Input */}
                <div className="relative">
                  <input
                    type="text"
                    placeholder="Search users by email or role..."
                    value={userSearchTerm}
                    onChange={(e) => setUserSearchTerm(e.target.value)}
                    className="w-full px-4 py-2 bg-gray-700 border border-gray-600 rounded-md text-gray-200 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-yellow-500 focus:border-transparent transition-colors duration-200 mb-2"
                  />
                  <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                    <svg className="h-5 w-5 text-gray-400" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z" clipRule="evenodd" />
                    </svg>
                  </div>
                </div>
              </div>

              {error && (
                <div className="bg-red-900/50 border border-red-700 text-red-200 px-4 py-3 rounded mb-4">
                  {error}
                </div>
              )}

              {showUserForm && (
                <div className="bg-gray-800 p-6 rounded-lg mb-6 border border-gray-700">
                  <form onSubmit={selectedUser ? handleUpdateUser : handleCreateUser}>
                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-300 mb-1">
                          Email
                        </label>
                        <input
                          type="email"
                          required
                          value={formData.email}
                          onChange={(e) =>
                            setFormData({ ...formData, email: e.target.value })
                          }
                          className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-md text-gray-200"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-300 mb-1">
                          Password {selectedUser && '(leave blank to keep current)'}
                        </label>
                        <div className="relative flex">
                          <input
                            type="password"
                            required={!selectedUser}
                            value={formData.password}
                            onChange={(e) =>
                              setFormData({ ...formData, password: e.target.value })
                            }
                            className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-l-md text-gray-200"
                          />
                          <button
                            type="button"
                            onClick={generateStrongPassword}
                            className="px-4 py-2 bg-gray-600 text-gray-200 hover:bg-gray-500 border-y border-gray-600"
                          >
                            Generate
                          </button>
                          <button
                            type="button"
                            onClick={handleCopyPassword}
                            className="px-4 py-2 bg-gray-600 text-gray-200 hover:bg-gray-500 border border-gray-600 rounded-r-md"
                            title="Copy password"
                          >
                            <ClipboardDocumentIcon className="h-5 w-5" />
                          </button>
                        </div>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-300 mb-1">
                          Role
                        </label>
                        <select
                          value={formData.role}
                          onChange={(e) =>
                            setFormData({ ...formData, role: e.target.value })
                          }
                          className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-md text-gray-200"
                        >
                          <option value="user">User</option>
                          <option value="admin">Admin</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-300 mb-1">
                          Subscription Date {selectedUser && '(leave blank to keep current)'}
                        </label>
                        <input
                          type="date"
                          value={formData.subscription_date}
                          onChange={(e) =>
                            setFormData({ ...formData, subscription_date: e.target.value })
                          }
                          className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-md text-gray-200"
                          min={new Date().toISOString().split('T')[0]}
                        />
                      </div>
                    </div>
                    <div className="mt-4 flex justify-end space-x-3">
                      <button
                        type="button"
                        onClick={() => setShowUserForm(false)}
                        className="px-4 py-2 border border-gray-600 rounded-md text-gray-300 hover:text-yellow-500 hover:border-yellow-500"
                      >
                        Cancel
                      </button>
                      <button
                        type="submit"
                        className="px-4 py-2 bg-yellow-500 text-gray-900 rounded-md hover:bg-yellow-600"
                      >
                        {selectedUser ? 'Update User' : 'Create User'}
                      </button>
                    </div>
                  </form>
                </div>
              )}

              <div className="bg-gray-800 shadow overflow-hidden rounded-lg border border-gray-700">
                <table className="min-w-full divide-y divide-gray-700">
                  <thead className="bg-gray-700">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                        <button
                          onClick={() => handleSort('email')}
                          className="flex items-center focus:outline-none"
                        >
                          Email
                          {sortConfig.key === 'email' && (
                            <svg
                              className={`ml-2 h-4 w-4 ${
                                sortConfig.direction === 'asc' ? '' : 'transform rotate-180'
                              }`}
                              xmlns="http://www.w3.org/2000/svg"
                              viewBox="0 0 20 20"
                              fill="currentColor"
                            >
                              <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
                            </svg>
                          )}
                        </button>
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                        Role
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                        Subscription
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                        <button
                          onClick={() => handleSort('created_at')}
                          className="flex items-center focus:outline-none"
                        >
                          Created At
                          {sortConfig.key === 'created_at' && (
                            <svg
                              className={`ml-2 h-4 w-4 ${
                                sortConfig.direction === 'asc' ? '' : 'transform rotate-180'
                              }`}
                              xmlns="http://www.w3.org/2000/svg"
                              viewBox="0 0 20 20"
                              fill="currentColor"
                            >
                              <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
                            </svg>
                          )}
                        </button>
                      </th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-300 uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-700">
                    {getSortedUsers(filteredUsers).map((user) => (
                      <tr key={user.id}>
                        <td className="px-6 py-4 whitespace-nowrap text-gray-300">
                          {user.email}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span
                            className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                              user.role === 'admin'
                                ? 'bg-yellow-500/20 text-yellow-500'
                                : 'bg-green-500/20 text-green-500'
                            }`}
                          >
                            {user.role}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          {user.role === 'admin' ? (
                            <span className="text-gray-400">N/A</span>
                          ) : (
                            <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                              user.subscription_date && new Date(user.subscription_date) > new Date()
                                ? 'bg-blue-500/20 text-blue-500'
                                : 'bg-red-500/20 text-red-500'
                            }`}>
                              {user.subscription_date 
                                ? new Date(user.subscription_date).toLocaleDateString()
                                : 'No subscription'}
                            </span>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-gray-300">
                          {new Date(user.created_at).toLocaleDateString()}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium space-x-3">
                          <button
                            onClick={() => handleEditUser(user)}
                            className="text-yellow-500 hover:text-yellow-400 inline-flex items-center"
                            title="Edit user"
                          >
                            <PencilIcon className="h-5 w-5" />
                          </button>
                          <button
                            onClick={() => handleDeleteUser(user.id)}
                            className="text-red-500 hover:text-red-400 inline-flex items-center"
                            title="Delete user"
                          >
                            <TrashIcon className="h-5 w-5" />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Companies Management */}
          {activeTab === 'companies' && (
            <div>
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl font-bold text-yellow-500">Manage Companies</h2>
                <button
                  onClick={() => {
                    setSelectedCompany(null);
                    setCompanyFormData({ name: '' });
                    setSelectedFile(null);
                    setShowCompanyForm(true);
                  }}
                  className="bg-yellow-500 text-gray-900 px-4 py-2 rounded-md hover:bg-yellow-600 transition-colors duration-200"
                >
                  Add New Company
                </button>
              </div>

              {error && (
                <div className="bg-red-900/50 border border-red-700 text-red-200 px-4 py-3 rounded mb-4">
                  {error}
                </div>
              )}

              {/* Show search and list only when not adding/editing */}
              {!showCompanyForm && (
                <>
                  <div className="mb-6">
                    <div className="relative">
                      <input
                        type="text"
                        placeholder="Enter at least 2 characters to search companies..."
                        className="w-full px-4 py-2 bg-gray-700 border border-gray-600 rounded-md text-gray-200 placeholder-gray-400"
                        value={searchTerm}
                        onChange={(e) => handleSearch(e.target.value)}
                      />
                      <div className="absolute inset-y-0 right-0 pr-3 flex items-center">
                        <svg className="h-5 w-5 text-gray-400" viewBox="0 0 20 20" fill="currentColor">
                          <path fillRule="evenodd" d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z" clipRule="evenodd" />
                        </svg>
                      </div>
                    </div>
                  </div>

                  {!searchTerm ? (
                    <div className="text-center text-gray-400 py-8 bg-gray-800 rounded-lg border border-gray-700">
                      <p>Enter company name in the search box above to find companies</p>
                    </div>
                  ) : searchTerm.length < 2 ? (
                    <div className="text-center text-gray-400 py-8 bg-gray-800 rounded-lg border border-gray-700">
                      <p>Please enter at least 2 characters to search</p>
                    </div>
                  ) : (
                    <div className="bg-gray-800 shadow overflow-hidden rounded-lg border border-gray-700">
                      <table className="min-w-full divide-y divide-gray-700">
                        <thead className="bg-gray-700">
                          <tr>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                              Name
                            </th>
                            <th className="px-6 py-3 text-right text-xs font-medium text-gray-300 uppercase tracking-wider">
                              Actions
                            </th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-700">
                          {companies.map((company) => (
                            <tr key={company.id}>
                              <td className="px-6 py-4 whitespace-nowrap text-gray-300">
                                {company.company_name}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium space-x-3">
                                <button
                                  onClick={() => window.open(`${API_BASE_URL}/pdfs/datas/${company.file_name}`, '_blank')}
                                  className="bg-blue-500 text-gray-900 p-1 rounded hover:bg-blue-400 inline-flex items-center"
                                  title="View PDF"
                                >
                                  <EyeIcon className="h-5 w-5" />
                                </button>
                                <button
                                  onClick={() => handleEditCompany(company)}
                                  className="bg-yellow-500 text-gray-900 p-1 rounded hover:bg-yellow-400 inline-flex items-center ml-2"
                                  title="Edit company"
                                >
                                  <PencilIcon className="h-5 w-5" />
                                </button>
                                <button
                                  onClick={() => handleDeleteCompany(company.id)}
                                  className="bg-red-500 text-gray-900 p-1 rounded hover:bg-red-400 inline-flex items-center ml-2"
                                  title="Delete company"
                                >
                                  <TrashIcon className="h-5 w-5" />
                                </button>
                              </td>
                            </tr>
                          ))}
                          {loading && (
                            <tr>
                              <td colSpan="2" className="px-6 py-4 whitespace-nowrap text-center">
                                <div className="flex justify-center items-center">
                                  <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-yellow-500"></div>
                                </div>
                              </td>
                            </tr>
                          )}
                          <tr ref={tableRef}><td></td></tr>
                        </tbody>
                      </table>
                    </div>
                  )}
                </>
              )}

              {/* Company Form */}
              {showCompanyForm && (
                <div className="bg-gray-800 p-6 rounded-lg mb-6 border border-gray-700">
                  <form onSubmit={handleCompanySubmit}>
                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-300 mb-1">
                          Company Name
                        </label>
                        <input
                          type="text"
                          required
                          value={companyFormData.name}
                          onChange={(e) =>
                            setCompanyFormData({ ...companyFormData, name: e.target.value })
                          }
                          className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-md text-gray-200"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-300 mb-1">
                          PDF File {selectedCompany && '(leave empty to keep current)'}
                        </label>
                        <input
                          type="file"
                          accept=".pdf"
                          onChange={(e) => setSelectedFile(e.target.files[0])}
                          className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-md text-gray-200"
                        />
                      </div>
                    </div>
                    <div className="mt-4 flex justify-end space-x-3">
                      <button
                        type="button"
                        onClick={() => setShowCompanyForm(false)}
                        className="px-4 py-2 border border-gray-600 rounded-md text-gray-300 hover:text-yellow-500 hover:border-yellow-500"
                      >
                        Cancel
                      </button>
                      <button
                        type="submit"
                        className="px-4 py-2 bg-yellow-500 text-gray-900 rounded-md hover:bg-yellow-600"
                      >
                        {selectedCompany ? 'Update Company' : 'Create Company'}
                      </button>
                    </div>
                  </form>
                </div>
              )}
            </div>
          )}

          {/* Products Management */}
          {activeTab === 'products' && (
            <div>
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl font-bold text-yellow-500">Manage Products</h2>
                <button
                  onClick={handleShowProductForm}
                  className="bg-yellow-500 text-gray-900 px-4 py-2 rounded-md hover:bg-yellow-600 transition-colors duration-200"
                >
                  Add New Product
                </button>
              </div>

              {error && (
                <div className="bg-red-900/50 border border-red-700 text-red-200 px-4 py-3 rounded mb-4">
                  {error}
                </div>
              )}

              {/* Show search and results only when not adding/editing */}
              {!showProductForm && (
                <div>
                  <div className="mb-6">
                    <div className="relative">
                      <input
                        type="text"
                        placeholder="Enter at least 2 characters to search products..."
                        className="w-full px-4 py-2 bg-gray-700 border border-gray-600 rounded-md text-gray-200 placeholder-gray-400"
                        value={productSearchQuery}
                        onChange={(e) => setProductSearchQuery(e.target.value)}
                      />
                      <div className="absolute inset-y-0 right-0 pr-3 flex items-center">
                        {loading ? (
                          <div className="animate-spin rounded-full h-5 w-5 border-t-2 border-b-2 border-gray-400"></div>
                        ) : (
                          <svg className="h-5 w-5 text-gray-400" viewBox="0 0 20 20" fill="currentColor">
                            <path fillRule="evenodd" d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z" clipRule="evenodd" />
                          </svg>
                        )}
                      </div>
                    </div>
                    <p className="mt-2 text-sm text-gray-400">Enter a product name to search, then click on a product to view its associated companies.</p>
                  </div>

                  {!productSearchQuery ? (
                    <div className="text-center text-gray-400 py-8">
                      <p>Type in the search box above to find products</p>
                    </div>
                  ) : loading ? (
                    <div className="flex justify-center py-8">
                      <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-yellow-500"></div>
                    </div>
                  ) : productSearchQuery.length < 2 ? (
                    <div className="text-center text-gray-400 py-8">
                      Please enter at least 2 characters to search
                    </div>
                  ) : products.length === 0 ? (
                    <div className="text-center text-gray-400 py-8">
                      No products found matching your search
                    </div>
                  ) : (
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                      {products.map((product) => (
                        <div
                          key={product.id}
                          className="bg-gray-800/80 p-6 rounded-lg border border-gray-700 hover:border-yellow-500 transition-colors duration-200 cursor-pointer"
                          onClick={() => navigate(`/admin/products/${product.id}`)}
                        >
                          <h3 className="text-yellow-500 font-medium mb-2">{product.name}</h3>
                          <p className="text-sm text-gray-400">
                            {(() => {
                              try {
                                if (!product.companies) return 'No associated companies';
                                const companiesData = typeof product.companies === 'string'
                                  ? JSON.parse(product.companies)
                                  : product.companies;
                                return `${Array.isArray(companiesData) ? companiesData.length : 0} associated ${
                                  companiesData.length === 1 ? 'company' : 'companies'
                                }`;
                              } catch (err) {
                                console.error('Error parsing companies:', err);
                                return 'Error displaying companies';
                              }
                            })()}
                          </p>
                          <div className="mt-4 flex justify-end space-x-2">
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleEditProduct(product);
                              }}
                              className="bg-yellow-500 text-gray-900 p-1 rounded hover:bg-yellow-400 inline-flex items-center"
                              title="Edit product"
                            >
                              <PencilIcon className="h-5 w-5" />
                            </button>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDeleteProduct(product.id);
                              }}
                              className="bg-red-500 text-gray-900 p-1 rounded hover:bg-red-400 inline-flex items-center"
                              title="Delete product"
                            >
                              <TrashIcon className="h-5 w-5" />
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Product Form */}
              {showProductForm && (
                <div className="bg-gray-800 p-6 rounded-lg mb-6 border border-gray-700">
                  <form onSubmit={selectedProduct ? handleUpdateProduct : handleCreateProduct}>
                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-300 mb-1">
                          Product Name
                        </label>
                        <input
                          type="text"
                          required
                          value={productFormData.name}
                          onChange={(e) =>
                            setProductFormData({ ...productFormData, name: e.target.value })
                          }
                          className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-md text-gray-200"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-300 mb-1">
                          Associated Companies ({productFormData.companies.length} selected)
                        </label>
                        <div className="space-y-2">
                          <input
                            type="text"
                            placeholder="Search companies..."
                            value={companySearchQuery}
                            onChange={(e) => setCompanySearchQuery(e.target.value)}
                            className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-md text-gray-200 mb-2"
                          />
                          <div className="max-h-60 overflow-y-auto space-y-2 bg-gray-700 border border-gray-600 rounded-md p-2">
                            {filteredCompanyList.map((company) => (
                              <div key={company.id} className="flex items-center space-x-2 p-2 hover:bg-gray-600 rounded">
                                <input
                                  type="checkbox"
                                  id={`company-${company.id}`}
                                  value={company.id}
                                  checked={productFormData.companies.includes(company.id)}
                                  onChange={(e) => {
                                    const newCompanies = e.target.checked
                                      ? [...productFormData.companies, company.id]
                                      : productFormData.companies.filter(id => id !== company.id);
                                    setProductFormData({ ...productFormData, companies: newCompanies });
                                  }}
                                  className="h-4 w-4 rounded border-gray-500 text-yellow-500 focus:ring-yellow-500"
                                />
                                <label
                                  htmlFor={`company-${company.id}`}
                                  className="text-gray-200 cursor-pointer flex-grow"
                                >
                                  {company.company_name}
                                </label>
                              </div>
                            ))}
                          </div>
                          {filteredCompanyList.length === 0 && companySearchQuery && (
                            <p className="text-gray-400 text-sm text-center py-2">
                              No companies found matching your search
                            </p>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="mt-4 flex justify-end space-x-3">
                      <button
                        type="button"
                        onClick={() => setShowProductForm(false)}
                        className="px-4 py-2 border border-gray-600 rounded-md text-gray-300 hover:text-yellow-500 hover:border-yellow-500"
                      >
                        Cancel
                      </button>
                      <button
                        type="submit"
                        className="px-4 py-2 bg-yellow-500 text-gray-900 rounded-md hover:bg-yellow-600"
                      >
                        {selectedProduct ? 'Update Product' : 'Create Product'}
                      </button>
                    </div>
                  </form>
                </div>
              )}
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

export default AdminDashboard;
